/**
 * StatusChip — SOC health/status state primitive.
 *
 * Design rules (DESIGN.md §6.2 health-badge model):
 *   - ONLY --status-* tokens. Never --sev-* (Active is not a severity).
 *   - Quiet pattern: no background tint, no shape mark. The LABEL TEXT is colored with
 *     the status-* token — color + the word itself provide redundant encoding, the same
 *     way DeviationCell works.
 *   - ok is intentionally kept neutral (text-primary): a nominal/healthy state carries
 *     no urgency and should not add saturated green to the at-rest screen. Color is an
 *     escalation budget; spending it on "everything is fine" dilutes the signal.
 *   - No uppercase, no bold, no tracking. Status is data, not an alarm.
 */

export type StatusState = "ok" | "warn" | "error" | "neutral";

interface StatusChipProps {
  status: StatusState;
  /** Optional override; defaults to the canonical health-model name. */
  label?: string;
}

const DEFAULT_LABEL: Record<StatusState, string> = {
  ok: "Nominal",
  warn: "Degraded",
  error: "Inactive",
  neutral: "Resolved",
};

// ok → text-primary: green on a healthy row is wasted budget (calm at rest).
// warn / error → status-* for urgency signal.
// neutral → text-muted: resolved/not-scheduled is the quietest state.
const STATUS_COLOR: Record<StatusState, string> = {
  ok: "text-primary",
  warn: "text-status-warn-text",
  error: "text-status-error-text",
  neutral: "text-muted",
};

const BASE_CLASSES =
  "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium leading-none";

export function StatusChip({ status, label }: StatusChipProps) {
  return (
    <span className={`${BASE_CLASSES} ${STATUS_COLOR[status]}`}>
      {label ?? DEFAULT_LABEL[status]}
    </span>
  );
}
